/**
 * 올린 HTML 을 **적용할 화면**에 맞춘다(2026-10-20) — 콘텐츠 상세와 [화면 맞춤] 창(올린 직후·목록)이 함께 쓴다.
 *
 * 운영자가 묻는 것은 둘뿐이다: **"저 화면에서 잘리나?"** 와 **"어떻게 하면 다 들어가나?"** 그래서 화면은 그 둘에 바로 답한다.
 *
 * 1. **적용할 화면을 고른다** — 등록된 화면이 보고한 크기(방향에 맞춰 세운 값)로. 고른 화면은 콘텐츠에 저장한다
 *    (`html_target_display_id`).
 * 2. **잘리는지 본다** — 그 화면에서 HTML 이 배치되는 창(`layoutViewport`)에 띄워 내용 끝까지 재고(재기 스크립트의 `full`),
 *    미리보기 위에 "✓ 다 보입니다" · "✕ 아래 245px 잘림" 을 바로 적고 잘리는 가장자리에 붉은 띠를 두른다.
 * 3. **[화면에 다 맞추기]** — 잘리지 않는 가장 큰 크기를 콘솔이 찾는다(`findWholeFitZoom`). 손으로는 **크기 −·+** 하나로 고친다.
 * 4. **[저장]** 해야 화면에 나간다.
 *
 * 기준 크기 W×H·맞춤 방식·다른 화면 미리보기는 **세부 설정**으로 접었다. 처음 판은 이 모두를 한 화면에 늘어놓아, 무엇을
 * 만져야 하는지 알기 어려웠다(2026-10-20 — 운영의 무재해 현황 HTML 을 스틱에 맞추려면 크기를 한 칸씩 내려 가며 미리보기를
 * 들여다봐야 했고, 잘리는지는 그림을 보고 짐작해야 했다).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Maximize2, Minus, Plus, RefreshCw } from 'lucide-react';
import { HTML_SIZE_MAX, HTML_SIZE_MIN, itemHtmlFit, type HtmlFit } from '@signageone/core';
import type { Asset, SignageOneClient } from '../api/client.js';
import { useAction, useAsync } from '../hooks.js';
import { Disclosure } from './Disclosure.js';
import { Badge, ErrorNote } from './ui.js';
import { focusFirstInvalid, NumberInput, numberProblem, type NumberRule } from './form/NumberInput.js';
import { HtmlFrame, HtmlThumb, htmlDesign, htmlFitSummary } from './HtmlContent.js';
import { findWholeFitZoom, layoutPatch, measureContent, measureHtmlLayout } from './htmlMeasure.js';
import {
  contentCut,
  defaultTargetKey,
  displayScreens,
  findScreen,
  fitPatch,
  fitReadout,
  fitUnsupported,
  layoutScale,
  layoutViewport,
  namesLabel,
  otherScreenSizes,
  PRESET_SCREENS,
  sameLookNote,
  visiblePercent,
  ZOOM_MAX,
  ZOOM_MIN,
  zoomedSize,
  type FitDraft,
  type Size,
} from './htmlScreens.js';

const FIT_OPTIONS: ReadonlyArray<{ value: HtmlFit; label: string; hint: string }> = [
  { value: 'auto', label: '맞춤', hint: '다 보이게 줄이거나 늘립니다 — 비율이 다른 쪽은 검은 띠가 생깁니다(권장).' },
  { value: 'cover', label: '채움', hint: '화면을 꽉 채웁니다 — 비율이 다른 쪽은 가장자리가 잘립니다.' },
  {
    value: 'none',
    label: '그대로',
    hint: '화면 크기 그대로 띄웁니다(기준 크기를 쓰지 않음) — HTML 이 화면보다 크면 오른쪽·아래가 잘리고, 작으면 빕니다.',
  },
];

const WIDTH_RULE: NumberRule = { label: '기준 너비', min: HTML_SIZE_MIN, max: HTML_SIZE_MAX, unit: 'px' };
const HEIGHT_RULE: NumberRule = { label: '기준 높이', min: HTML_SIZE_MIN, max: HTML_SIZE_MAX, unit: 'px' };

/** −·+ 한 번에 바꾸는 크기(%p). */
const ZOOM_STEP = 5;

/** 크기를 바꾼 뒤 잘림을 다시 재기까지 기다리는 시간 — 밀기를 끄는 동안 창을 여러 번 띄우지 않게. */
const CHECK_DELAY_MS = 450;

type SizeText = { width: number | null; height: number | null };

/** 잘림 재기의 결과 — 재는 중이면 'checking', 잴 수 없었으면 cut 이 null. */
type CutCheck = { key: string; cut: { bottom: number; right: number } | null } | 'checking';

/** 잘린 양을 사람 말로 — "아래 245px · 오른쪽 12px". */
function cutText(cut: { bottom: number; right: number }): string {
  return [cut.bottom > 0 ? `아래 ${cut.bottom}px` : '', cut.right > 0 ? `오른쪽 ${cut.right}px` : ''].filter(Boolean).join(' · ');
}

export function HtmlScreenFit({
  client,
  asset,
  canWrite,
  onChanged,
  onDirtyChange,
}: {
  client: SignageOneClient;
  asset: Asset;
  canWrite: boolean;
  onChanged: () => void;
  /** 저장하지 않은 조정이 있는가 — 창이 닫기 전에 묻는다. */
  onDirtyChange?: (dirty: boolean) => void;
}): ReactNode {
  const displays = useAsync(() => client.allDisplays(), [client]);
  const savedTarget = asset.html_target_display_id ?? null;
  const screens = useMemo(() => displayScreens(displays.data?.items ?? [], savedTarget), [displays.data, savedTarget]);
  /** 사람이 고른 화면(고르기 전에는 null — 정해 둔 화면·흔한 크기를 보여 준다). */
  const [chosenKey, setChosenKey] = useState<string | null>(null);
  const targetKey = chosenKey ?? defaultTargetKey(screens, savedTarget);
  const target = findScreen(screens, targetKey) ?? PRESET_SCREENS[0]!;
  const screen: Size = target.size ?? PRESET_SCREENS[0]!.size!;

  const saved: FitDraft = useMemo(
    () => ({ fit: asset.html_fit ?? 'auto', size: htmlDesign(asset) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 저장된 값이 바뀔 때만
    [asset.html_fit, asset.width, asset.height],
  );
  const [fit, setFit] = useState<HtmlFit>(saved.fit);
  /** 기준 크기 칸 — null 이면 늘고 주는 HTML(화면 크기 그대로). */
  const [size, setSize] = useState<SizeText | null>(saved.size);
  const [showErrors, setShowErrors] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const action = useAction();
  const [measuring, setMeasuring] = useState(false);
  const [measureFailed, setMeasureFailed] = useState(false);
  /** [화면에 다 맞추기] 가 도는 중, 그리고 그 결과 한마디. */
  const [fitting, setFitting] = useState(false);
  const [fitNote, setFitNote] = useState<{ text: string; tone: 'ok' | 'warn' } | null>(null);

  // 저장된 값이 바뀌면(저장·다시 재기) 조정 칸을 그 값으로 — 지금 조정이 곧 저장된 값이다.
  const savedKey = `${saved.fit}|${saved.size?.width ?? ''}x${saved.size?.height ?? ''}`;
  useEffect(() => {
    setFit(saved.fit);
    setSize(saved.size);
    setShowErrors(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 저장된 값이 바뀔 때만
  }, [savedKey]);

  const widthError = size ? numberProblem(size.width, WIDTH_RULE) : null;
  const heightError = size ? numberProblem(size.height, HEIGHT_RULE) : null;
  const sizeInvalid = Boolean(widthError || heightError);
  /** 치는 도중의 값(예: "12")은 범위 밖이다 — 그때는 마지막으로 맞았던 값으로 그린다. */
  const lastValid = useRef<Size | null>(saved.size);
  if (size && !sizeInvalid) lastValid.current = { width: size.width!, height: size.height! };
  const draft: FitDraft = {
    fit,
    size: size === null ? null : sizeInvalid ? (lastValid.current ?? saved.size) : { width: size.width!, height: size.height! },
  };
  const chosenDisplayId = chosenKey === null ? savedTarget : target.displayId;
  const patch = fitPatch({ ...saved, targetDisplayId: savedTarget }, { ...draft, targetDisplayId: chosenDisplayId });
  const dirty = sizeInvalid || Object.keys(patch).length > 0;
  const lookChanged = patch.html_fit !== undefined || patch.html_size !== undefined;
  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

  // 바뀐 값이 미리보기에 닿으면 테두리를 잠깐 밝힌다 — 그림이 비슷해도 "방금 바뀌었다" 가 보이게.
  const look = `${targetKey}|${draft.fit}|${draft.size?.width ?? ''}x${draft.size?.height ?? ''}`;
  const [flash, setFlash] = useState(false);
  const firstLook = useRef(look);
  useEffect(() => {
    if (look === firstLook.current) return;
    firstLook.current = look;
    setFlash(true);
    const timer = window.setTimeout(() => setFlash(false), 700);
    return () => window.clearTimeout(timer);
  }, [look]);

  /*
   * 잘리는가 — HTML 이 배치되는 창에 띄워 내용 끝까지 잰다. 같은 창은 다시 재지 않는다(밀기를 오가도). 바꾼 직후에는 잠깐
   * 기다린다(`CHECK_DELAY_MS`) — 한 번 재는 데 2초쯤 걸린다.
   */
  const viewport = layoutViewport(screen, draft);
  const checkKey = `${viewport.width}x${viewport.height}`;
  const checked = useRef(new Map<string, { bottom: number; right: number } | null>());
  const [check, setCheck] = useState<CutCheck>('checking');
  useEffect(() => {
    const url = asset.preview_url;
    if (!url) return;
    const known = checked.current.get(checkKey);
    if (known !== undefined) {
      setCheck({ key: checkKey, cut: known });
      return;
    }
    setCheck('checking');
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void measureContent(url, viewport).then((measure) => {
        const cut = measure ? contentCut(measure) : null;
        checked.current.set(checkKey, cut);
        if (!cancelled) setCheck({ key: checkKey, cut });
      });
    }, CHECK_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 창 크기가 바뀔 때만
  }, [asset.preview_url, checkKey]);

  const measure = async (): Promise<void> => {
    if (!asset.preview_url) return;
    setMeasuring(true);
    setMeasureFailed(false);
    try {
      const layout = await measureHtmlLayout(asset.preview_url);
      if (!layout) {
        setMeasureFailed(true);
        return;
      }
      await action.run(() => client.updateAsset(asset.id, layoutPatch(layout)), onChanged);
    } finally {
      setMeasuring(false);
    }
  };

  // 아직 재지 않았으면 연 김에 잰다 — 올린 직후 목록이 못 잰 것(창을 바로 닫았다 등)도 여기서 잡는다.
  const autoMeasured = useRef(false);
  useEffect(() => {
    if (!canWrite || autoMeasured.current || asset.html_measured_at !== null || !asset.preview_url) return;
    autoMeasured.current = true;
    void measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 열었을 때 한 번
  }, [canWrite, asset.html_measured_at, asset.preview_url]);

  /** 잘리지 않는 가장 큰 크기로 — 콘솔이 크기를 바꿔 가며 재서 찾는다. 맞춤 방식은 '맞춤' 으로 돌린다. */
  const fitWhole = async (): Promise<void> => {
    if (!asset.preview_url) return;
    setFitting(true);
    setFitNote(null);
    try {
      const zoom = await findWholeFitZoom(asset.preview_url, screen);
      if (zoom === undefined) {
        setFitNote({ text: '재지 못했습니다 — 세부 설정에서 기준 크기를 직접 정하세요', tone: 'warn' });
      } else if (zoom === null) {
        setFitNote({ text: `${ZOOM_MIN}% 로 줄여도 잘립니다 — 세부 설정에서 기준 크기를 직접 정하세요`, tone: 'warn' });
      } else {
        setFit('auto');
        setSize(zoomedSize(screen, { fit: 'auto', size: null }, zoom));
        setShowErrors(false);
        setFitNote({ text: `잘리지 않는 가장 큰 크기 ${zoom}% 로 맞췄습니다 — [저장] 해야 화면에 나갑니다`, tone: 'ok' });
      }
    } finally {
      setFitting(false);
    }
  };

  const save = (): void => {
    if (sizeInvalid) {
      setShowErrors(true);
      focusFirstInvalid(formRef.current);
      return;
    }
    if (Object.keys(patch).length === 0) return;
    void action.run(
      () => client.updateAsset(asset.id, patch),
      () => {
        setFitNote(null);
        onChanged();
      },
      '화면 맞춤을 저장했습니다',
    );
  };
  const revert = (): void => {
    setFit(saved.fit);
    setSize(saved.size);
    setChosenKey(null);
    setShowErrors(false);
    setFitNote(null);
  };

  /** HTML 이 실제로 그린 크기 — 콘솔이 잰 것만(사람이 정한 뒤에는 모른다). 기준 크기를 이보다 작게 잡으면 잘린다. */
  const content = asset.html_size_manual ? null : saved.size;
  const readout = fitReadout(screen, draft, content);
  const itemFit = itemHtmlFit(draft.fit, draft.size?.width, draft.size?.height);
  const percent = visiblePercent(screen, draft);
  const savedPercent = visiblePercent(screen, saved);
  const zoomLocked = draft.size !== null && draft.fit === 'none';
  const zoom = (next: number): void => {
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(next)));
    setSize(zoomedSize(screen, draft, clamped));
    setShowErrors(false);
    setFitNote(null);
  };
  const sameLook = sameLookNote(screen, draft.size);
  const others = otherScreenSizes(screens, screen);
  const fitOption = FIT_OPTIONS.find((option) => option.value === fit) ?? FIT_OPTIONS[0]!;
  const source = measuring
    ? '크기를 재는 중…'
    : dirty && (patch.html_size !== undefined || sizeInvalid)
      ? '조정 중'
      : asset.html_measured_at === null || asset.html_measured_at === undefined
        ? '아직 재지 않음'
        : asset.html_size_manual
          ? '직접 정함'
          : '자동 감지';

  /*
   * 잘림 판정 — 재는 중·잴 수 없음·다 보임·잘림. 잘린 양은 화면 픽셀로 옮겨 적는다(배치 창이 화면에서 몇 배로 보이는가).
   * "채움" 이 가장자리를 자르는 것은 재기와 상관없는 계산이라 아래 작은 줄(`readout`)이 노랑으로 말한다.
   */
  const current = check !== 'checking' && check.key === checkKey ? check : null;
  const scale = layoutScale(screen, draft);
  const cut =
    current?.cut && (current.cut.bottom > 0 || current.cut.right > 0)
      ? { bottom: Math.round(current.cut.bottom * scale), right: Math.round(current.cut.right * scale) }
      : null;
  const verdict: 'checking' | 'unknown' | 'ok' | 'cut' =
    fitting || !current ? 'checking' : current.cut === null ? 'unknown' : cut ? 'cut' : 'ok';
  const chip =
    verdict === 'checking'
      ? { text: '잘리는지 확인 중…', tone: 'is-checking' }
      : verdict === 'ok'
        ? { text: '✓ 다 보입니다', tone: 'is-ok' }
        : verdict === 'cut' && cut
          ? { text: `✕ ${cutText(cut)} 잘림`, tone: 'is-cut' }
          : null;

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50" ref={formRef}>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2">
        <label htmlFor={`html-target-${asset.id}`} className="w-16 shrink-0 text-[12.5px] font-medium text-slate-600">
          적용할 화면
        </label>
        <select
          id={`html-target-${asset.id}`}
          className="input min-w-0 max-w-full flex-1 sm:max-w-[420px]"
          value={targetKey}
          onChange={(event) => {
            setChosenKey(event.target.value);
            setFitNote(null);
          }}
        >
          {screens.length > 0 ? (
            <optgroup label="등록된 화면">
              {screens.map((option) => (
                <option key={option.key} value={option.key} disabled={!option.size}>
                  {option.name} — {option.detail}
                </option>
              ))}
            </optgroup>
          ) : null}
          <optgroup label="흔한 화면 크기">
            {PRESET_SCREENS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.name} — {option.detail}
              </option>
            ))}
          </optgroup>
        </select>
        {chosenDisplayId !== savedTarget ? (
          <Badge tone="warn">저장 안 됨</Badge>
        ) : savedTarget && target.displayId === savedTarget ? (
          <Badge tone="info">적용할 화면으로 정함</Badge>
        ) : null}
        {displays.loading ? <span className="hint">등록된 화면을 읽는 중…</span> : null}
        {displays.error ? <span className="text-[12px] text-amber-700">등록된 화면을 읽지 못했습니다 — 흔한 크기로 봅니다</span> : null}
      </div>

      {canWrite ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2.5">
          <span className="w-16 shrink-0 text-[12.5px] font-medium text-slate-600">크기</span>
          <button
            type="button"
            className="btn btn-tiny btn-icon"
            aria-label="작게"
            title="작게 — 화면에서 더 작게 보입니다"
            disabled={fitting || zoomLocked || (percent ?? 100) <= ZOOM_MIN}
            onClick={() => zoom((percent ?? 100) - ZOOM_STEP)}
          >
            <Minus className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
          </button>
          <input
            type="range"
            className="w-32 accent-slate-700 sm:w-44"
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            step={5}
            value={percent ?? 100}
            disabled={fitting || zoomLocked}
            aria-label="크기(%)"
            aria-valuetext={`${percent ?? 100}%`}
            onChange={(event) => zoom(Number(event.target.value))}
          />
          <button
            type="button"
            className="btn btn-tiny btn-icon"
            aria-label="크게"
            title="크게 — 화면에서 더 크게 보입니다(너무 크면 잘립니다)"
            disabled={fitting || zoomLocked || (percent ?? 100) >= ZOOM_MAX}
            onClick={() => zoom((percent ?? 100) + ZOOM_STEP)}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
          </button>
          <strong className="w-11 text-[13px] tabular-nums text-slate-800" data-testid="html-fit-zoom">
            {zoomLocked ? '—' : `${percent ?? 100}%`}
          </strong>
          <button
            type="button"
            className="btn btn-sm"
            disabled={fitting || !asset.preview_url}
            title="잘리지 않는 가장 큰 크기를 콘솔이 크기를 바꿔 가며 재서 찾습니다"
            onClick={() => void fitWhole()}
          >
            <Maximize2 className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
            {fitting ? '맞추는 중…' : '화면에 다 맞추기'}
          </button>
          <span className="flex-1" />
          <button type="button" className="btn btn-primary btn-sm" disabled={action.pending || !dirty} onClick={save}>
            저장
          </button>
          <button type="button" className="btn btn-sm" disabled={action.pending || !dirty} onClick={revert}>
            되돌리기
          </button>
        </div>
      ) : null}

      {asset.preview_url ? (
        <div className={`html-fit-preview${flash ? ' is-changed' : ''}`}>
          <HtmlFrame
            key={targetKey}
            url={asset.preview_url}
            title={asset.title}
            className="h-[min(360px,48vh)] min-h-[220px] w-full bg-slate-100"
            screen={screen}
            design={draft.size}
            fit={itemFit}
            cut={cut ? { bottom: cut.bottom > 0, right: cut.right > 0 } : null}
          />
          {chip ? (
            <span className={`html-fit-chip ${chip.tone}`} data-testid="html-fit-chip">
              {chip.text}
            </span>
          ) : null}
        </div>
      ) : (
        <HtmlThumb asset={asset} className="aspect-video w-full" />
      )}

      <div className="space-y-0.5 border-t border-slate-200 px-3 py-2 text-[13px]" aria-live="polite">
        <p>
          <strong className="font-semibold text-slate-800">
            {target.name} {screen.width}×{screen.height}
          </strong>
          <span className="text-slate-500"> · </span>
          {verdict === 'cut' && cut ? (
            <span className="font-medium text-red-700" data-testid="html-fit-verdict">
              {cutText(cut)}가 잘립니다{canWrite ? ' — [화면에 다 맞추기] 를 누르면 잘리지 않는 가장 큰 크기로 맞춥니다' : ''}
            </span>
          ) : verdict === 'ok' ? (
            <span className="font-medium text-emerald-700" data-testid="html-fit-verdict">
              내용이 다 보입니다
            </span>
          ) : verdict === 'checking' ? (
            <span className="text-slate-500" data-testid="html-fit-verdict">
              {fitting ? '잘리지 않는 가장 큰 크기를 찾는 중…' : '잘리는지 확인하는 중…'}
            </span>
          ) : (
            <span className={readout.tone === 'warn' ? 'text-amber-700' : 'text-slate-700'} data-testid="html-fit-verdict">
              {readout.text}
            </span>
          )}
        </p>
        <p className="text-[12px] text-slate-500" data-testid="html-fit-readout">
          {readout.text}
        </p>
        {fitNote ? (
          <p className={`text-[12.5px] ${fitNote.tone === 'warn' ? 'text-amber-700' : 'text-emerald-700'}`}>{fitNote.text}</p>
        ) : null}
        {dirty ? (
          <p className="text-[12.5px] text-amber-700">
            {lookChanged ? `저장된 크기 ${savedPercent ?? 100}% → 지금 ${percent ?? 100}% · ` : ''}저장하지 않았습니다
          </p>
        ) : null}
        {draft.size && fitUnsupported(target.appVersion) ? (
          <p className="text-[12.5px] text-amber-700">
            이 화면의 에이전트({target.appVersion})는 화면 맞춤을 모릅니다 — 0.7.18 이상으로 올리기 전까지는 크기와 상관없이 화면 크기
            그대로 띄웁니다.
          </p>
        ) : null}
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      </div>

      {canWrite ? (
        <Disclosure
          title="세부 설정"
          summary="기준 크기 · 맞춤 방식 · 다른 화면"
          defaultOpen={false}
          forceOpen={showErrors && sizeInvalid}
          className="border-t border-slate-200 px-3 py-1"
        >
          <div className="space-y-2.5 pb-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-16 shrink-0 text-[12.5px] font-medium text-slate-600">기준 크기</span>
              <div className="seg seg-sm" role="group" aria-label="기준 크기">
                <button
                  type="button"
                  className="seg-btn"
                  aria-pressed={size !== null}
                  onClick={() => setSize(size ?? draft.size ?? saved.size ?? { ...screen })}
                >
                  고정 크기
                </button>
                <button type="button" className="seg-btn" aria-pressed={size === null} onClick={() => setSize(null)}>
                  늘고 주는 HTML
                </button>
              </div>
              {size ? (
                <>
                  <NumberInput
                    rule={WIDTH_RULE}
                    value={size.width}
                    invalid={showErrors && widthError !== null}
                    aria-label="기준 너비(px)"
                    onChange={(width) => setSize({ ...size, width })}
                  />
                  <span className="text-slate-500">×</span>
                  <NumberInput
                    rule={HEIGHT_RULE}
                    value={size.height}
                    invalid={showErrors && heightError !== null}
                    aria-label="기준 높이(px)"
                    onChange={(height) => setSize({ ...size, height })}
                  />
                  <button
                    type="button"
                    className="btn btn-tiny"
                    disabled={size.width === screen.width && size.height === screen.height}
                    title="이 화면에서 줄이거나 늘리지 않고 그립니다"
                    onClick={() => setSize({ ...screen })}
                  >
                    이 화면 크기로({screen.width}×{screen.height})
                  </button>
                </>
              ) : (
                <span className="text-[12.5px] text-slate-600">화면마다 그 화면 크기 그대로 그립니다</span>
              )}
              <span className="text-[12px] text-slate-500">· {source}</span>
              <button
                type="button"
                className="btn btn-tiny"
                disabled={action.pending || measuring || !asset.preview_url}
                title="고정 크기인지, 늘고 주는 HTML 인지 다시 재서 곧바로 저장합니다"
                onClick={() => void measure()}
              >
                <RefreshCw className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                자동으로 다시 재기
              </button>
            </div>
            {sizeInvalid ? (
              <p className={showErrors ? 'field-error' : 'text-[12.5px] text-amber-700'} role={showErrors ? 'alert' : undefined}>
                {widthError ?? heightError}
                {showErrors ? null : ' — 미리보기는 마지막으로 맞았던 크기로 보입니다'}
              </p>
            ) : null}
            {measureFailed ? (
              <p className="text-[12.5px] text-amber-700">재지 못했습니다(문서가 그려지지 않음) — 기준 크기를 직접 정할 수 있습니다.</p>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <span className="w-16 shrink-0 text-[12.5px] font-medium text-slate-600">맞춤 방식</span>
              <div className="seg seg-sm" role="group" aria-label="맞춤 방식">
                {FIT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="seg-btn"
                    aria-pressed={fit === option.value}
                    disabled={size === null}
                    title={option.hint}
                    onClick={() => setFit(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <span className="text-[12.5px] text-slate-600">
                {size === null ? '늘고 주는 HTML 은 화면을 그대로 채웁니다 — 맞춤 방식이 필요 없습니다' : fitOption.hint}
              </span>
            </div>
            {sameLook ? <p className="text-[12.5px] text-sky-700">{sameLook}</p> : null}

            {others.length > 0 ? (
              <div>
                <p className="mb-2 text-[12.5px] font-medium text-slate-600">
                  다른 화면에서는{' '}
                  <span className="font-normal text-slate-500">— 이 설정은 이 콘텐츠가 나가는 모든 화면에 함께 걸립니다</span>
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {others.map((other) => {
                    const line = fitReadout(other.size, draft, content);
                    return (
                      <figure key={`${other.size.width}x${other.size.height}`} className="m-0 min-w-0">
                        {asset.preview_url ? (
                          <HtmlFrame
                            url={asset.preview_url}
                            title={`${asset.title} — ${other.size.width}×${other.size.height}`}
                            className="h-[110px] w-full rounded bg-slate-100"
                            screen={other.size}
                            design={draft.size}
                            fit={itemFit}
                          />
                        ) : null}
                        <figcaption className="mt-1 text-[12px] leading-snug">
                          <span className="font-medium text-slate-700">
                            {namesLabel(other.names)} · {other.size.width}×{other.size.height}
                          </span>
                          <br />
                          <span className={line.tone === 'warn' ? 'text-amber-700' : 'text-slate-500'}>{line.text}</span>
                        </figcaption>
                      </figure>
                    );
                  })}
                </div>
              </div>
            ) : null}
            <p className="hint">
              화면에서는 샌드박스 안에서 돌며(플레이어·기기·콘솔에 손대지 못함) 밖으로는 https 만 엽니다. 화면 맞춤은 에이전트 0.7.18 부터
              따릅니다.
            </p>
          </div>
        </Disclosure>
      ) : (
        <p className="border-t border-slate-200 px-3 py-2 text-[13px] text-slate-600">{htmlFitSummary(asset)}</p>
      )}
    </div>
  );
}
